import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button, ChatThinkingLoader, PanelHeader, Popover, PopoverContent, PopoverTrigger } from '@/components';
import { useERSContext, useMapContext } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import {
  capitalizeWords,
  cn,
  INTENT_DETERMINATION_SYSTEM_PROMPT,
  sanitizeIncidents,
  sanitizeResponders,
  systemPromptForDataAnalysis,
} from '@/lib';
import { getIncidents, getResponders } from './DataAccessLayer';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const definedSuggestedQueries = [
  {
    category: 'Incident Lookup',
    description: 'Fetch and summarize active or past incidents by name, type, or location.',
    queries: [
      {
        text: 'Show all active incidents',
        type: 'action',
        prompt: `You are an emergency management assistant. Fetch all active incidents from the dataset. 
Return a concise summary with total count, types of incidents, and their short descriptions.`,
        filter: { status: { $ne: 'resolved' } },
      },
      {
        text: 'List all incidents reported today.',
        type: 'action',
        prompt: `List all incidents with start_timestamp equal to today's date. 
Summarize them in a readable table with incident ID, type, and location.`,
        filter: {
          creationTimestamp: { $gte: Math.floor(Date.now() / 1000) },
        },
      },
      {
        text: 'List past incidents',
        type: 'action',
        prompt: `Retrieve all incidents whose status is 'closed' or 'resolved'. 
Show their summary including incident ID, type, resolution timestamp, and assigned responder.`,
        filter: { title: { $eq: '546 - Fire' } },
      },
      {
        text: 'Find incidents reported last hour',
        type: 'action',
        prompt: `Fetch all incidents created today from the dataset and group them by type.`,
        filter: {
          creationTimestamp: { $gte: Math.floor(Date.now() / 1000) - 3600 },
        },
      },
      {
        text: 'Show me all active fire incidents in Zone A.',
        type: 'example',
      },
    ],
  },
  {
    category: 'Responder Lookup',
    description: 'Fetch and summarize active or past incidents by ID, type, or location.',
    queries: [
      {
        text: 'Show all responders',
        type: 'action',
        prompt: `You are an emergency management assistant. Fetch all active responders from the dataset.
Return a concise summary with total count, types of incidents, and their short descriptions.`,
        filter: {}, // presence: { $eq: 'disconnected' }
      },
    ],
  },
  {
    category: 'Incident Details',
    description: 'Provide detailed status, assigned responder, and timestamps.',
    queries: [
      {
        text: 'What’s the current status of Incident #1023?',
        type: 'example',
      },
      {
        text: 'Show responder assigned to Incident #1012.',
        type: 'example',
      },
      {
        text: 'When was Incident #120 closed?',
        type: 'example',
      },
      {
        text: 'Show me details of Incident #1023.',
        type: 'example',
      },
    ],
  },
];

export const AIChatBot = ({ children, isOpen, toggle, onClose }) => {
  const { users, incidents } = useERSContext();
  const { focusIncident, focusResponder } = useMapContext();
  const { callAPI } = useAIProvider();

  const [suggestedQueries, setSuggestedQueries] = useState([...definedSuggestedQueries]);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Ask me about active incidents, resource availability, situational analysis, or operational insights!`,
      timestamp: new Date(),
      type: 'Greeting',
      options: suggestedQueries?.map(item => item?.category),
    },
  ]);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * This function is helpful to identify which category is selected
   * @param category - we used that parameter for taking category from user
   */
  const handleCategorySelect = category => {
    setMessages(prev => [
      ...prev,
      {
        role: 'assistant',
        content: `You’re now exploring ${category}. Here are some example queries:`,
        timestamp: new Date(),
        category,
      },
    ]);
  };

  const handleIncidentClick = data => {
    focusIncident(data);
  };

  const handleResponderClick = data => {
    focusResponder(data);
  };

  const systemPrompt = `
      You are an intent detection and response engine for an AI assistant used in an incident management system. Your name is OPTI (Operations Intelligence)

      Your goals:
        1. Detect the user's intent.
        2. Determine if the message relates to an incident type (Medical, Fire, Patrol, or Accident).
        3. Identify irrelevant, arrogant, or rude messages and classify them as "out_of_scope" with type = null.
        4. Respond appropriately with a short assistant message.

      Intent categories:
      - "process_data_query" — The user is asking about incidents, reports, analytics, or data.
      - "respond_greeting" — The user greets in a friendly, relevant manner (e.g., "hi", "hello", "good morning").
      - "out_of_scope" — The message is sarcastic, arrogant, dismissive, irrelevant, or not related to incident management.

      Respond ONLY in this JSON format:
      {
        "intent": "process_data_query"|"respond_greeting"|"out_of_scope",
        "type": "medical"|"fire"|"patrol"|"accident"|null,
        "message": "string — short assistant message relevant to the detected intent"
      }

      Examples:
      User: "Hi there"
      Response:
      {
        "intent": "respond_greeting",
        "type": "greeting",
        "message": "Hello! How can I assist you with incidents today?"
      }

      User: "Show me fire incidents"
      Response:
      {
        "intent": "process_data_query",
        "type": "fire",
        "message": "Sure! Fetching the latest fire incident details..."
      }

      User: "GN"
      Response:
      {
        "intent": "respond_greeting",
        "type": null,
        "message": "Good night! Talk to you soon."
      }

      User: "GN whatever this is dumb"
      Response:
      {
        "intent": "out_of_scope",
        "type": null,
        "message": "I'm here to help with incidents and reports, but I might not be able to handle that request. Would you like me fetch details of an incident instead?"
      }
    `;

  const detectFollowUp = async (userMessage, messages) => {
    const detectorPrompt = `
        You are a system designed to determine whether a user's message is a follow-up to the previous conversation.

        In this system, conversations alternate between the user and the system. The user may ask a new question or continue from a previous one.

        Your task is to analyze the most recent user message in the context of the previous conversation and respond in the following format:

        {
          "follow_up": <true|false>,
          "follow_up_query": "<only include this field if follow_up is true, and rewrite the message to include necessary context from the previous conversation>"
        }

        The follow_up_query should be a self-contained version of the current message that includes relevant context from the conversation history to make it understandable on its own.

        ### Previous Conversation:
        ${JSON.stringify(messages.slice(-5), null, 2)}

        ### Current Message:
        ${userMessage}
        `;

    const message = [{ role: 'user', content: detectorPrompt }];
    const response = await callAPI(message, { temperature: 0 });

    try {
      return JSON.parse(response);
    } catch {
      return { follow_up: false };
    }
  };

  /**
   * This function is help to identify intent of input-chat
   * @param message - we used that parameter for taking message from user
   */
  const detectIntent = async message => {
    const input = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: message },
    ];

    const response = await callAPI(input);
    try {
      const parsed = JSON.parse(response);
      return parsed;
    } catch {
      return {
        intent: 'process_data_query',
        message: 'I’m processing your request related to incidents.',
      };
    }
  };

  const processUserMessage = async queryToProcess => {
    try {
      const now = Math.floor(Date.now() / 1000);
      const tzName = dayjs.tz.guess();

      const messages = [
        { role: 'system', content: INTENT_DETERMINATION_SYSTEM_PROMPT },
        {
          role: 'user',
          content: queryToProcess + `The current time is ${now} and my current local timezone is ${tzName}.`,
        },
      ];

      const response = await callAPI(messages, { temperature: 0.6 });

      const cleanedResponse = response
        .replace(/```json/g, '')
        .replace(/```/g, '')
        .trim();

      const result = JSON.parse(cleanedResponse);

      if (result.mode === 'ai') {
        const sanitizedIncidents = sanitizeIncidents(incidents, result?.requiredIncidentKeys);
        const sanitizedResponders = sanitizeResponders(users, result?.requiredResponderKeys);

        const messageForProcessingData = [
          { role: 'system', content: systemPromptForDataAnalysis },
          {
            role: 'user',
            content: `INCIDENT DATA:
            ${JSON.stringify(sanitizedIncidents, null, 2)}

            RESPONDER DATA:
            ${JSON.stringify(sanitizedResponders, null, 2)}

            QUERY TO ANALYZE:
            ${queryToProcess}

            Please analyze the data thoroughly and provide insights with clear reasoning.`,
          },
        ];

        const resp = await callAPI(messageForProcessingData, { temperature: 0.6 });

        return resp ? resp : "Sorry, I couldn't identify the action.";
      }

      if (result.mode === 'dal') {
        switch (result.dataset) {
          case 'incidents':
            return getIncidents({
              incidents: incidents,
              filter: result?.filter,
              onIncidentClick: handleIncidentClick,
            });

          case 'responders':
            const responders = users.filter(u => u?.permission?.includes('MobileUnit'));
            return getResponders({
              responders: responders,
              filter: result?.filter,
              onResponderClick: handleResponderClick,
            });

          default:
            return "Sorry, I couldn't identify the action.";
        }
      }

      return "Sorry, I couldn't identify the action.";
    } catch (error) {
      console.error('ERROR in processUserMessage:', error);
      return "Sorry, I couldn't identify the action.";
    }
  };

  /**
   * This function is help to identify where chat to AI or local data
   * @param data - we used that parameter for two way for object and string
   * @param isUserInput - boolean to identify this function is triggered from chat-input or chat-action
   */
  const sendToAI = async (data, isUserInput = false, category) => {
    const userMessage = typeof data === 'string' ? data : data.text;

    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setIsLoading(true);
    setError('');

    try {
      if (isUserInput) {
        // Detect if it's a follow-up (only for user-typed input)
        const followUpResult = await detectFollowUp(userMessage, messages);
        const queryToProcess = followUpResult.follow_up ? followUpResult.follow_up_query : userMessage;
        const verifiedIntent = await detectIntent(queryToProcess);

        if (verifiedIntent?.intent === 'respond_greeting' || verifiedIntent?.intent === 'out_of_scope') {
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: verifiedIntent?.message,
              timestamp: new Date(),
              type: verifiedIntent?.type,
              options: suggestedQueries?.map(item => item?.category),
            },
          ]);
          return;
        } else {
          const resolvedData = await processUserMessage(queryToProcess);
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: resolvedData,
              timestamp: new Date(),
              category: data.category,
            },
          ]);
        }
      } else {
        if (category === 'Incident Lookup') {
          const resolvedData = getIncidents({
            incidents: incidents,
            filter: data?.filter,
            onIncidentClick: handleIncidentClick,
          });
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: resolvedData,
              timestamp: new Date(),
              category: data.category,
            },
          ]);
        } else if (category === 'Responder Lookup') {
          const resolvedData = getResponders({
            responders: users.filter(
              u => u?.permission?.includes('MobileUnit') && u?.lastKnownLocation?.lon && u?.lastKnownLocation?.lat
            ),
            filter: data?.filter,
            onResponderClick: handleResponderClick,
          });
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              content: resolvedData,
              timestamp: new Date(),
              category: data.category,
            },
          ]);
        }
      }
    } catch (err) {
      setError('Failed to get response. Please check your AI provider setup or API key.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    await sendToAI(userMessage, true);
  };

  return (
    <Popover open={isOpen} onOpenChange={toggle}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={12}
        className="max-w-[22.5rem] w-full p-0 flex flex-col overflow-hidden rounded-lg shadow-sm shadow-strong bg border [border-image:linear-gradient(94.73deg,_#B6F4FF_1.74%,_#6C94FF_73.83%,_#A497FF_96.59%)_1]"
        aria-label="AI Assistant Chat Panel"
      >
        <section className="flex flex-col max-h-[32.5rem]" role="dialog" aria-live="polite">
          {/* Header */}
          <PanelHeader
            title={capitalizeWords('Operations Intelligence')}
            as="h2"
            showClose
            onClickChange={onClose}
            isGradient
          />

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto p-3 space-y-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Chat messages"
            id="chat-messages"
          >
            {messages.map((message, index) => (
              <div key={index} className={cn('flex flex-col', message.role === 'user' && 'items-end')}>
                {message.role === 'user' ? (
                  <article
                    className="max-w-[90%] p-3 bg-primary text-inverse rounded-tl-lg rounded-bl-lg rounded-br-lg text-sm leading-relaxed whitespace-pre-wrap"
                    aria-label="user message"
                  >
                    {message.content}
                  </article>
                ) : (
                  <div className="max-w-[90%] p-[1px] rounded-tr-lg rounded-bl-lg rounded-br-lg bg-gradient-to-r from-[#B6F4FF] via-[#6C94FF] to-[#A497FF]">
                    <article
                      className="bg text p-3 rounded-tr-lg rounded-bl-lg rounded-br-lg text-sm leading-relaxed whitespace-pre-wrap"
                      aria-label="assistant message"
                    >
                      {message.content}
                    </article>
                  </div>
                )}
                {message?.type && (
                  <div className="max-w-[90%] mt-3 flex flex-col gap-3 bg-transparent">
                    {message?.options?.map(opt => (
                      <button
                        key={opt}
                        className="px-3 py-1 text-xs border rounded-lg transition w-fit text-primary hover:bg-primary-hover hover:border-info hover:text cursor-pointer"
                        onClick={() => handleCategorySelect(opt)}
                        disabled={isLoading}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {message.category && (
                  <div className="max-w-[90%] mt-3 flex flex-col gap-3 bg-transparent">
                    {suggestedQueries
                      .find(q => q.category === message.category)
                      ?.queries?.map((s, idx) => (
                        <button
                          key={idx}
                          className={cn(
                            'px-3 py-1 text-xs border rounded-lg transition w-fit',
                            s.type === 'action'
                              ? 'text-primary hover:bg-primary-hover hover:border-info hover:text cursor-pointer'
                              : 'text cursor-default'
                          )}
                          onClick={() => s.type === 'action' && sendToAI(s, false, message.category)}
                          disabled={isLoading || s.type !== 'action'}
                        >
                          {s.text}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && <ChatThinkingLoader />}
            <div ref={messagesEndRef} />
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-6 py-2 bg-error-strong border-t border-error-active" role="alert">
              <div className="flex items-center gap-2 text-error text-sm">
                <AlertCircle className="w-4 h-4" aria-hidden="true" />
                {error}
              </div>
            </div>
          )}

          {/* Input Field */}
          <div className="flex flex-row bg border-t border-border px-4 py-3">
            <div className="flex flex-1 items-center border border-border rounded-lg focus-within:ring-2 focus-within:ring-interactive-active focus-within:border-transparent transition">
              <input
                id="chat-input"
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="How can I help you?"
                className="flex-1 px-3 py-2 bg-transparent text placeholder:text-placeholder placeholder:text-sm rounded-l-lg focus:outline-none disabled:bg-disabled disabled:cursor-not-allowed"
                disabled={isLoading}
                aria-label="Type your message"
              />
              <Button
                onClick={handleSend}
                className="rounded-r-lg border-border focus:outline-none"
                variant="ghost"
                disabled={isLoading || !inputValue.trim()}
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="w-5 h-5" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </section>
      </PopoverContent>
    </Popover>
  );
};
